import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ApplicationState } from "store/state";
import { getIdleTimeMs, initializeIdleTimer } from "util/idle-timer";
import { buildVersion } from "util/version";

const BANNER_DELAY_MS = 1000 * 60 * 10;
const STALE_TAB_IDLE_MS = 1000 * 60 * 30;
const IDLE_CHECK_MS = 1000 * 60;

export const AutoReload: React.FunctionComponent = () => {
    const serverVersion = useSelector(
        (state: ApplicationState) => state.serverBuild.serverVersion,
    );
    const needsReload = buildVersion !== "unknown" && serverVersion !== buildVersion;
    const location = useLocation();
    const [showBanner, setShowBanner] = React.useState(false);

    const reload = React.useCallback(() => {
        window.location.reload();
    }, []);

    React.useEffect(() => {
        if (!needsReload) {
            setShowBanner(false);
            return undefined;
        }
        const timer = setTimeout(() => setShowBanner(true), BANNER_DELAY_MS);
        return () => clearTimeout(timer);
    }, [needsReload]);

    const initialLocationRef = React.useRef(location);
    React.useEffect(() => {
        if (needsReload && location !== initialLocationRef.current) {
            reload();
        }
    }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const handlePageShow = (e: PageTransitionEvent) => {
            if (e.persisted && needsReload) {
                reload();
            }
        };
        window.addEventListener("pageshow", handlePageShow);
        return () => window.removeEventListener("pageshow", handlePageShow);
    }, [needsReload, reload]);

    React.useEffect(() => {
        if (!needsReload) {
            return undefined;
        }
        const stopIdleTimer = initializeIdleTimer();
        const interval = window.setInterval(() => {
            if (getIdleTimeMs() >= STALE_TAB_IDLE_MS) {
                window.clearInterval(interval);
                reload();
            }
        }, IDLE_CHECK_MS);
        return () => {
            window.clearInterval(interval);
            stopIdleTimer();
        };
    }, [needsReload, reload]);

    if (showBanner) {
        return (
            <div className="p-4 font-bold text-lg bg-gray-800 text-white text-center">
                A new version is available!{" "}
                <button
                    onClick={reload}
                    className="underline hover:text-gray-300"
                    type="button">
                    Click to reload
                </button>
                .
            </div>
        );
    }

    if (needsReload) {
        return (
            <div className="fixed top-1 right-1 z-50 w-1 h-1 rounded-full bg-brand-500" />
        );
    }

    return null;
};
